import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { confirmDialog } from '../../lib/ui/dialogs.js';
import { analyticsConsentGiven, setAnalyticsConsent } from '../../lib/analytics/track.js';
import {
  clearDebugEvents,
  flushLocalDb,
  listDebugEvents,
  listRecoverablePendingOpReconciliations,
  openLocalDb,
  requeueRecoverablePendingOpReconciliation,
  type DebugEventRow,
  type RecoverablePendingOpReconciliation,
} from '../../lib/db/local.js';
import { readLsLog, clearLsLog } from '../../lib/debug/debugLog.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { resetPullCursors } from '../../lib/sync/pullCursorRecovery.js';
import { getSupabase } from '../../lib/supabase.js';

interface ServerTrace {
  id: number;
  ts: string;
  request_id: string;
  function_name: string;
  step: string;
  duration_ms: number | null;
  level: 'info' | 'warn' | 'error';
  payload: Record<string, unknown>;
}

type Tab = 'session' | 'server' | 'export';

function safeQueueJson(value: unknown): string {
  return JSON.stringify(value, (key, item: unknown) =>
    /assertion|token|password|secret|base64/i.test(key) ? '[omesso]' : item, 2);
}


const LEVEL_COLOR: Record<string, string> = {
  info: 'medium',
  warn: 'warning',
  error: 'danger',
};

export function DiagnosticsPage(): JSX.Element {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = profile?.active_tenant_id ?? null;
  const [tab, setTab] = useState<Tab>('session');
  const [sessionRows, setSessionRows] = useState<DebugEventRow[]>([]);
  const [recoverableRows, setRecoverableRows] = useState<RecoverablePendingOpReconciliation[]>([]);
  const [queueRows, setQueueRows] = useState<Array<{ queue: string; detail: string }>>([]);
  const [serverRows, setServerRows] = useState<ServerTrace[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [analyticsOn, setAnalyticsOn] = useState(analyticsConsentGiven());
  const [resyncBusy, setResyncBusy] = useState(false);
  const [recoveringId, setRecoveringId] = useState<number | null>(null);

  async function refreshSession(): Promise<void> {
    setLoading(true);
    try {
      const [rows, recoverable] = await Promise.all([
        listDebugEvents(300),
        listRecoverablePendingOpReconciliations(),
      ]);
      setRecoverableRows(recoverable);
      const db = await openLocalDb(null);
      const queue: Array<{ queue: string; detail: string }> = [];
      const sources = [
        ['Operazione', 'SELECT id, kind, payload, attempts, last_error, created_at FROM pending_ops', 'payload'],
        ['Comando magazzino', `
          SELECT c.command_id, c.tenant_id, c.digest, c.status, c.attempts,
                 c.last_error, c.conflict_json, c.command_json, c.master_effects_json,
                 c.optimistic_event_ids, c.queued_at,
                 (SELECT json_group_array(json(e.event_json))
                    FROM pending_stock_command_events e
                   WHERE e.command_id = c.command_id) AS events_json,
                 (SELECT value FROM meta
                   WHERE key = 'shared_tablet.stock_master_operator_v1:' || c.command_id)
                   AS operator_identity_json
            FROM pending_stock_commands c`, 'command_json'],
        ['Operazione atomica', 'SELECT request_id, kind, tenant_id, status, attempts, last_error, conflict_json, request_json, prerequisite_request_id, queued_at FROM pending_atomic_rpcs_v1', 'request_json'],
        ['Esito etichetta', 'SELECT intent_id, tenant_id, command_id, production_id, outcome, status, attempts, last_error, queued_at FROM pending_label_print_outcomes_v1', ''],
        ['Intento etichetta', "SELECT id, tenant_id, version, row FROM masters_local WHERE table_name = 'production_label_print_intents_v1' AND json_extract(row, '$.state') IN ('pending','failed')", 'row'],
        ['Operazione isolata', "SELECT id, kind, payload_json, reason, attempts, status, created_at FROM pending_op_reconciliations_v1 WHERE status = 'reconciliation_required'", 'payload_json'],
        ['Comando isolato', "SELECT command_id, tenant_id, digest, command_json, events_json, master_effects_json, reason, status, resolution, created_at FROM stock_command_reconciliations_v1 WHERE status = 'reconciliation_required'", 'command_json'],
      ];
      for (const [label, sql, jsonField] of sources) {
        const result = await db.query(sql);
        for (const raw of result.values ?? []) {
          const row = { ...raw } as Record<string, unknown>;
          for (const field of [jsonField, 'master_effects_json', 'events_json', 'operator_identity_json']) {
            if (field && typeof row[field] === 'string') {
              try { row[field] = JSON.parse(row[field] as string) as unknown; } catch { /* keep malformed data visible */ }
            }
          }
          const payload = row[jsonField] as Record<string, unknown> | undefined;
          if ((row.tenant_id ?? payload?.tenant_id ?? (payload?.patch as Record<string, unknown> | undefined)?.tenant_id) !== tenantId) continue;
          queue.push({ queue: label, detail: safeQueueJson(row) });
        }
      }
      setQueueRows(queue);
      // Fallback su localStorage se SQLite restituisce vuoto (DB locale chiusa
      // o non aperta). I log restano accessibili per la diagnostica.
      if (rows.length === 0) {
        const ls = readLsLog();
        const lsRows: DebugEventRow[] = ls.slice(-300).reverse().map((e, idx) => ({
          id: -idx - 1,
          ts: e.ts,
          area: e.area,
          level: e.level,
          message: e.message,
          payload: e.payload ?? null,
        }));
        setSessionRows(lsRows);
      } else {
        setSessionRows(rows);
      }
    } finally {
      setLoading(false);
    }
  }

  async function refreshServer(): Promise<void> {
    if (!tenantId) return;
    setLoading(true);
    try {
      // Filter rilassato: include anche tenant_id=null perché alcuni edge
      // path scrivevano tenant_id null (vedi bug log-ingest pre-fix). Sono
      // comunque log dell'utente corrente, RLS già garantisce isolamento.
      const { data, error } = await getSupabase()
        .from('debug_traces')
        .select('id, ts, request_id, function_name, step, duration_ms, level, payload')
        .or(`tenant_id.eq.${tenantId},tenant_id.is.null`)
        .order('ts', { ascending: false })
        .limit(300);
      if (error) {
        setToast(`Errore: ${error.message}`);
        return;
      }
      setServerRows((data ?? []) as ServerTrace[]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (tab === 'session') void refreshSession();
    if (tab === 'server') void refreshServer();
  }, [tab, tenantId]);

  async function handleClear(): Promise<void> {
    await clearDebugEvents();
    setSessionRows([]);
    setToast('Log sessione cancellato');
  }

  async function recoverContractRejectedEvent(
    row: RecoverablePendingOpReconciliation,
  ): Promise<void> {
    const label = row.event.kind === 'cleaning_done' ? 'pulizia' : 'temperatura';
    const ok = await confirmDialog(
      `Recupera questo evento di ${label} con il suo UUID originale. ` +
      'Il server era stato aggiornato dopo il rifiuto del vecchio contratto: ' +
      'una pulizia già confermata viene collegata alla registrazione esistente. Procedere?',
      { header: 'Recupera evento HACCP', okText: 'Recupera' },
    );
    if (!ok) return;
    setRecoveringId(row.id);
    try {
      const recovered = await requeueRecoverablePendingOpReconciliation(row.id);
      if (!recovered) {
        setToast('Evento non più disponibile per il recupero. Aggiorna la lista.');
        return;
      }
      await getSyncEngine().tick();
      await refreshSession();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      setToast('Evento recuperato e sincronizzazione avviata.');
    } catch (err) {
      setToast(`Recupero non riuscito: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRecoveringId(null);
    }
  }

  // T-127 (rev 2 fix): forza re-sync completo della cache eventi.
  // Sintomi: lo Storico etichette/temperature/pulizie di un tenant è vuoto
  // pur avendo dati sul server. Root cause: il cursore incrementale
  // `last_rev` (meta locale) è avanzato sotto un account/tenant diverso, e
  // i `sync-pull` successivi filtrano `server_rev > last_rev` → gli eventi
  // vecchi del tenant attuale non vengono mai ripullati.
  //
  // Ordine delle operazioni (intenzionale per robustezza):
  //  1. setMeta('last_rev','0')   → CRITICO. Deve riuscire perché è la sola
  //                                  cosa che sblocca i pull successivi. Se
  //                                  fallisce, il bottone non ha effetto
  //                                  utile.
  //  2. DELETE events_local       → best-effort. Su SQLite con molte righe
  //                                  può colpire SQL_OP_TIMEOUT_MS=8s in
  //                                  local.ts e auto-resettare la
  //                                  connection ("connection X doesn't
  //                                  exist"). Se fallisce, il sync-pull a
  //                                  since_rev=0 fa comunque INSERT OR
  //                                  REPLACE su client_uuid e sovrascrive
  //                                  qualsiasi riga vecchia.
  //  3. tick()                    → forza un sync-pull immediato (non aspetta
  //                                  l'intervallo da 30s).
  //  4. invalidateQueries         → la UI si aggiorna.
  //
  // NON tocca `pending_ops` (push in coda) né `masters_local` (anagrafiche
  // già coerenti). Il filtro server-side `.in('tenant_id', claims.tenants)`
  // in sync-pull garantisce che si ricevano solo eventi dei tenant a cui
  // l'utente appartiene davvero.
  async function forceFullResync(): Promise<void> {
    if (resyncBusy) return;
    const ok = await confirmDialog(
      'Resetta la sincronizzazione e riscarica TUTTI gli eventi dal server. ' +
      'Le etichette stampate, temperature, pulizie e altri storici HACCP ' +
      'torneranno dopo il re-sync (10-30 secondi a seconda della rete). ' +
      'Le modifiche locali ancora in coda di invio NON vengono toccate. Procedere?',
      { header: 'Re-sincronizzazione', okText: 'Procedi' },
    );
    if (!ok) return;
    setResyncBusy(true);
    setToast('Re-sync in corso…');
    let cursorReset = false;
    let wipeOk = false;
    let wipeError: string | null = null;
    try {
      // Step 1 — critico. Se fallisce, abortiamo. Azzeriamo ENTRAMBI i cursori:
      // il legacy `last_rev` E la mappa per-tenant `last_rev_by_tenant` (T-182).
      // Senza il secondo, su client per-tenant il re-sync ripartiva comunque dal
      // cursore alto → gli eventi vecchi non tornavano (storico ancora tagliato).
      await resetPullCursors();
      cursorReset = true;
      // Step 2 — best-effort. DELETE su tabella grande può andare in timeout.
      try {
        const db = await openLocalDb(null);
        await db.run(`DELETE FROM events_local`);
        await flushLocalDb();
        wipeOk = true;
      } catch (err) {
        wipeError = err instanceof Error ? err.message : String(err);
        // Non rilanciamo: il sync-pull con since_rev=0 sovrascrive comunque
        // le righe esistenti via INSERT OR REPLACE su client_uuid PK.
      }
      // Step 3 — tick immediato. Se la connection è stata appena chiusa
      // dall'auto-reset, il prossimo open la ricrea.
      await getSyncEngine().tick();
      await queryClient.invalidateQueries({ queryKey: ['events'] });
      await queryClient.invalidateQueries({ queryKey: ['masters'] });
      if (wipeOk) {
        setToast('Re-sync completato. Apri lo Storico per vedere i dati.');
      } else {
        setToast(
          `Cursore resettato, eventi in arrivo dal server. Cache locale non svuotata (${wipeError ?? 'timeout'}) — sovrascritta dai dati nuovi.`,
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (cursorReset) {
        setToast(
          `Cursore resettato ma tick fallito: ${msg}. Chiudi e riapri l'app: il prossimo sync automatico recupererà i dati.`,
        );
      } else {
        setToast(`Errore re-sync: ${msg}. Riprova oppure chiudi e riapri l'app.`);
      }
    } finally {
      setResyncBusy(false);
    }
  }

  async function handleExport(): Promise<void> {
    const session = await listDebugEvents(500);
    let server: ServerTrace[] = [];
    if (tenantId) {
      const { data } = await getSupabase()
        .from('debug_traces')
        .select('id, ts, request_id, function_name, step, duration_ms, level, payload')
        .or(`tenant_id.eq.${tenantId},tenant_id.is.null`)
        .order('ts', { ascending: false })
        .limit(500);
      server = (data ?? []) as ServerTrace[];
    }
    const bundle = {
      generated_at: new Date().toISOString(),
      tenant_id: tenantId,
      user_id: profile?.id ?? null,
      session,
      server,
      queues: queueRows.map((row) => ({ queue: row.queue, detail: JSON.parse(row.detail) as unknown })),
    };
    const json = JSON.stringify(bundle, null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setToast('Diagnostica copiata negli appunti');
    } catch {
      // Fallback estremo: textarea + execCommand (vecchie WebView).
      try {
        const ta = document.createElement('textarea');
        ta.value = json;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setToast('Diagnostica copiata negli appunti');
      } catch {
        setToast('Impossibile copiare — apri la pagina su PC');
      }
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/settings" />
          </IonButtons>
          <IonTitle>Diagnostica</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)}>
            <IonSegmentButton value="session">
              <IonLabel>Sessione</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="server">
              <IonLabel>Server</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="export">
              <IonLabel>Esporta</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <IonList>
          <IonItem lines="full">
            <IonLabel className="ion-text-wrap">
              <strong>Telemetria di prodotto</strong>
              <IonNote style={{ display: 'block', fontSize: 12 }}>
                Raccoglie in modo anonimo come usi le funzioni dell&apos;app per
                migliorarla. Nessun dato personale. Disattivabile in qualsiasi
                momento (svuota anche la coda non inviata).
              </IonNote>
            </IonLabel>
            <IonToggle
              slot="end"
              checked={analyticsOn}
              onIonChange={(e) => {
                const on = e.detail.checked;
                setAnalyticsConsent(on);
                setAnalyticsOn(on);
                setToast(on ? 'Telemetria attivata' : 'Telemetria disattivata');
              }}
            />
          </IonItem>
        </IonList>
        {tab === 'session' && (
          <>
            <div style={{ padding: 8, display: 'flex', gap: 8 }}>
              <IonButton size="small" onClick={() => void refreshSession()}>
                Aggiorna
              </IonButton>
              <IonButton size="small" color="medium" fill="outline" onClick={() => void handleClear()}>
                Pulisci
              </IonButton>
              {loading && <IonSpinner />}
            </div>
            <IonList inset>
              <IonItem><IonLabel className="ion-text-wrap">
                <strong>Coda del locale ({queueRows.length})</strong>
                <p>Operazioni conservate su questo dispositivo. Il dettaglio mostra l’ultimo errore e le dipendenze.</p>
              </IonLabel></IonItem>
              {queueRows.map((row, index) => <IonItem key={index}><IonLabel className="ion-text-wrap">
                <details><summary>{row.queue}</summary><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 12 }}>{row.detail}</pre></details>
              </IonLabel></IonItem>)}
            </IonList>
            {recoverableRows.length > 0 && (
              <IonList inset>
                <IonItem color="warning" lines="full">
                  <IonLabel className="ion-text-wrap">
                    <strong>Eventi HACCP recuperabili</strong>
                    <IonNote style={{ display: 'block', fontSize: 12 }}>
                      Questi eventi erano stati messi in quarantena dal vecchio
                      contratto di sincronizzazione. Puoi reinviarli uno alla volta:
                      tutti gli altri rifiuti restano protetti in quarantena.
                    </IonNote>
                  </IonLabel>
                </IonItem>
                {recoverableRows.map((row) => (
                  <IonItem key={row.id} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <strong>{row.event.kind === 'cleaning_done' ? 'Pulizia' : 'Temperatura'}</strong>
                      <IonNote style={{ display: 'block', fontSize: 12 }}>
                        {row.reason}
                      </IonNote>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      disabled={recoveringId !== null}
                      onClick={() => void recoverContractRejectedEvent(row)}
                    >
                      {recoveringId === row.id ? <IonSpinner name="crescent" /> : 'Reinvia'}
                    </IonButton>
                  </IonItem>
                ))}
              </IonList>
            )}
            <IonList>
              {sessionRows.length === 0 && (
                <IonItem><IonLabel><IonNote>Nessun evento.</IonNote></IonLabel></IonItem>
              )}
              {sessionRows.map((r) => (
                <IonItem key={r.id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ fontSize: 11, margin: 0 }}>
                      <span style={{ color: `var(--ion-color-${LEVEL_COLOR[r.level] ?? 'medium'})` }}>
                        {r.level.toUpperCase()}
                      </span>{' '}
                      · {r.ts} · <strong>{r.area}</strong>
                    </p>
                    <p style={{ margin: '2px 0' }}>{r.message}</p>
                    {r.payload != null && (
                      <pre style={{ fontSize: 10, margin: 0, whiteSpace: 'pre-wrap', color: 'var(--ion-color-medium)' }}>
                        {typeof r.payload === 'string' ? r.payload : JSON.stringify(r.payload)}
                      </pre>
                    )}
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </>
        )}
        {tab === 'server' && (
          <>
            <div style={{ padding: 8, display: 'flex', gap: 8 }}>
              <IonButton size="small" onClick={() => void refreshServer()}>
                Aggiorna
              </IonButton>
              {loading && <IonSpinner />}
            </div>
            <IonList>
              {serverRows.length === 0 && (
                <IonItem><IonLabel><IonNote>Nessuna riga server.</IonNote></IonLabel></IonItem>
              )}
              {serverRows.map((r) => (
                <IonItem key={r.id} lines="full">
                  <IonLabel className="ion-text-wrap">
                    <p style={{ fontSize: 11, margin: 0 }}>
                      <span style={{ color: `var(--ion-color-${LEVEL_COLOR[r.level] ?? 'medium'})` }}>
                        {r.level.toUpperCase()}
                      </span>{' '}
                      · {r.ts} · <strong>{r.function_name}</strong> · {r.step}
                      {r.duration_ms != null && ` · ${r.duration_ms}ms`}
                    </p>
                    <p style={{ fontSize: 10, margin: '2px 0', color: 'var(--ion-color-medium)' }}>
                      req {r.request_id.slice(0, 8)}
                    </p>
                    <pre style={{ fontSize: 10, margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(r.payload)}
                    </pre>
                  </IonLabel>
                </IonItem>
              ))}
            </IonList>
          </>
        )}
        {tab === 'export' && (
          <div style={{ padding: 16 }}>
            <p>
              Copia negli appunti un bundle JSON con gli ultimi 500 eventi locali e gli ultimi 500
              eventi server di questo tenant. Utile da incollare nei ticket di supporto.
            </p>
            <IonButton expand="block" onClick={() => void handleExport()}>
              Copia diagnostica
            </IonButton>
            <p style={{ marginTop: 24 }}>
              {Capacitor.isNativePlatform()
                ? 'Per verificare e installare gli aggiornamenti, apri Impostazioni e usa “Verifica aggiornamenti”.'
                : 'Gli aggiornamenti vengono scaricati quando sei connesso a Internet. Salva il lavoro, chiudi tutte le schede e finestre di TrackEAT, compresa l’app aperta dalla schermata Home, poi riaprila. Offline puoi continuare a usare la versione già disponibile.'}
            </p>
            <p style={{ marginTop: 24 }}>
              Svuota la cache eventi del telefono e riscarica TUTTO dal server
              (etichette, temperature, pulizie, scarichi magazzino, …). Da usare
              quando uno Storico HACCP risulta vuoto pur avendo dati sul server
              — tipico dopo essere stati aggiunti come consulente a un nuovo
              locale, o dopo aver cambiato account su questo telefono.
            </p>
            <IonButton
              expand="block"
              color="danger"
              disabled={resyncBusy}
              onClick={() => void forceFullResync()}
            >
              {resyncBusy ? 'Re-sync in corso…' : 'Forza re-sync DB locale'}
            </IonButton>
          </div>
        )}
      </IonContent>
      <IonToast
        isOpen={!!toast}
        message={toast ?? ''}
        duration={2000}
        onDidDismiss={() => setToast(null)}
      />
    </IonPage>
  );
}
